"use client";

import Link from "next/link";
import { Lock, LogIn, UserRound } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { RichContent } from "@/components/rich-content";
import { formatNumber } from "@/lib/format-datetime";
import { cn } from "@/lib/utils";
import type { Locale } from "@/lib/i18n";
import type { LearnAccessLevel } from "@/features/learn/api";
import type { LearnLessonAccessState, LearnLessonTeaser } from "./api";
import { signInHref as buildSignInHref } from "@/lib/post-login-redirect";

/**
 * What a reader sees when a lesson is not theirs yet (`LN-RDR01`, docs/35 §5
 * item 5).
 *
 * The spec is precise about the shape and about two things it must not be:
 * "heading, first paragraph, then a card that lists the lesson's `h2`s and the
 * two buttons (track, Learn Pro). **Never a blurred body, never a countdown.**"
 *
 * Both of those are honoured by what is not in this file. There is no body to
 * blur: the server sends the first paragraph and the outline and keeps the rest
 * (`learn-lesson-teaser.ts`), so a reader with dev tools finds a card and a list
 * of headings and nothing else. And there is no timer, because a countdown on a
 * paid lesson is a pressure device and the founder's rule of docs/35 §5 item 7
 * is that the reader carries no promo mechanism but the Playground strip.
 *
 * Two states share this card because they differ by one question. A `core`
 * lesson asks for an account, which is free; a `pro` lesson asks for the track
 * or for Learn Pro. Both buttons now go somewhere real: the track page carries
 * the track's own price and buy path, and `/learn/pro` carries the subscription
 * (`LN-SUB01`, which is what this comment used to say was not built yet).
 *
 * **The card follows the lesson, not the ask** (`LN-LCH01`). The server
 * answers `sign_in` for every locked lesson to a stranger, because it cannot
 * tell what a signed-out reader holds, so a Pro lesson read signed out used to
 * get the account card and its "This lesson is free, with an account". A Pro
 * lesson now always gets the Pro card (the track or Learn Pro, docs/35 §2 and
 * §5 item 5), and a signed-out reader gets one more line under it, to sign in
 * in case they already hold one of the two.
 *
 * **Not known yet is not locked** (`LN-RDR03`, founder 2026-10-08). The page is
 * cached from the anonymous answer, so every gated lesson first arrives as
 * `sign_in`, and a signed-in learner saw this card's "Sign in" blink for about
 * a second before their own answer replaced it. While that answer is on its
 * way (`pending`) the card keeps the first paragraph and the outline, which are
 * the same for everyone, and draws a neutral, busy header in place of the ask.
 */
export function LearnLockCard({
  access,
  learnAccess,
  teaser,
  contentLang,
  locale,
  trackSlug,
  lessonTitle,
  signInRedirect,
  pending = false,
}: {
  access: Exclude<LearnLessonAccessState, "granted">;
  /** The lesson's own level (docs/35 §2); `pro` draws the Pro card even when the ask is `sign_in`. */
  learnAccess?: LearnAccessLevel;
  teaser: LearnLessonTeaser | null;
  contentLang: "en" | "bn";
  locale: Locale;
  trackSlug: string;
  lessonTitle: string;
  /** Where sign-in comes back to. Passed in, never read off `window`: this card
   * is in the server HTML of a locked lesson, and a value that differs between
   * the two renders is a hydration mismatch. */
  signInRedirect: string;
  /** The reader's own answer has not landed yet: no ask, a neutral header. */
  pending?: boolean;
}) {
  const t = useTranslations("learn.reader");
  const isPro = access === "pro" || learnAccess === "pro";
  const signedOut = access === "sign_in";

  const firstParagraph =
    contentLang === "bn" && teaser?.firstParagraph_bn
      ? teaser.firstParagraph_bn
      : (teaser?.firstParagraph_en ?? "");
  const outline =
    contentLang === "bn" && teaser?.outline_bn.length
      ? teaser.outline_bn
      : (teaser?.outline_en ?? []);

  const trackHref = `/${locale}/learn/${trackSlug}`;
  const signInHref = buildSignInHref(locale, signInRedirect);

  /** The lesson's headings: the same for every reader, so drawn whether the ask is known or not. */
  const outlineBlock =
    outline.length > 0 ? (
      <div className="px-5 pb-2 pt-4 sm:px-6">
        <p className="mb-1 font-[family-name:var(--font-heading)] text-sm font-bold text-neutral-900">
          {t("lockOutlineHeading", { title: lessonTitle })}
        </p>
        <ol>
          {outline.map((heading, index) => (
            <li
              className="flex gap-3 border-t border-neutral-100 py-2.5 text-[14.5px] text-neutral-800"
              key={heading}
            >
              <span aria-hidden className="pt-0.5 font-mono text-xs text-neutral-400">
                {formatNumber(index + 1, contentLang)}
              </span>
              <span>{heading}</span>
            </li>
          ))}
        </ol>
      </div>
    ) : null;

  return (
    <div>
      {firstParagraph ? (
        <RichContent className="mb-8" html={`<p>${firstParagraph}</p>`} />
      ) : null}

      {pending ? (
        <section
          aria-busy="true"
          aria-labelledby="learn-lock-pending"
          className="overflow-hidden rounded-[var(--radius)] border border-neutral-200 bg-white"
          data-testid="learn-lock-pending"
        >
          <div className="flex gap-4 border-b border-neutral-100 bg-neutral-50 p-5 sm:p-6">
            <span aria-hidden className="h-11 w-11 shrink-0 animate-pulse rounded-[var(--radius)] bg-neutral-200" />
            <div className="min-w-0 flex-1 space-y-2.5 pt-1">
              <p className="text-sm font-medium text-neutral-600" id="learn-lock-pending">
                {t("lockChecking")}
              </p>
              <span aria-hidden className="block h-3 w-3/4 animate-pulse rounded bg-neutral-200" />
              <span aria-hidden className="block h-3 w-1/2 animate-pulse rounded bg-neutral-200" />
            </div>
          </div>
          {outlineBlock}
        </section>
      ) : (
      /* `LN-UI05`: the ask on a tinted header, the outline under it as a
          numbered list. Sky for "an account", the brand tint for Pro, so the
          two asks never look alike. */
      <section
        aria-labelledby="learn-lock"
        className={cn(
          "overflow-hidden rounded-[var(--radius)] border bg-white",
          isPro ? "border-orange-200" : "border-sky-200",
        )}
      >
        <div
          className={cn(
            "flex flex-col gap-4 border-b p-5 sm:flex-row sm:p-6",
            isPro ? "border-orange-100 bg-orange-50" : "border-sky-100 bg-sky-50",
          )}
        >
          <span
            aria-hidden
            className={cn(
              "flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius)] border bg-white",
              isPro ? "border-orange-200 text-primary-ink" : "border-sky-200 text-sky-700",
            )}
          >
            {isPro ? <Lock className="h-5 w-5" /> : <UserRound className="h-5 w-5" />}
          </span>
          <div className="min-w-0">
            <p
              className={cn(
                "text-xs font-bold uppercase tracking-[0.12em]",
                isPro ? "text-primary-ink" : "text-sky-700",
              )}
            >
              {isPro ? t("lockProEyebrow") : t("lockCoreEyebrow")}
            </p>
            <h2
              className="mt-1.5 font-[family-name:var(--font-heading)] text-xl font-bold text-neutral-900"
              id="learn-lock"
            >
              {isPro ? t("lockProTitle") : t("lockCoreTitle")}
            </h2>
            <p className="mt-1.5 text-[14.5px] leading-relaxed text-neutral-600">
              {isPro ? t("lockProBody") : t("lockCoreBody")}
            </p>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {isPro ? (
                <>
                  <Button asChild className="h-11 px-5 text-[15px] font-bold">
                    <Link href={trackHref}>{t("lockBuyTrack")}</Link>
                  </Button>
                  <Button asChild className="h-11 px-5 text-[15px] font-bold" variant="secondary">
                    <Link href={`/${locale}/learn/pro`}>{t("lockGetPro")}</Link>
                  </Button>
                  {signedOut ? (
                    <p className="basis-full text-[13.5px] text-neutral-600">
                      <Link className="font-medium text-primary-ink underline hover:no-underline" href={signInHref}>
                        {t("lockProSignIn")}
                      </Link>
                    </p>
                  ) : null}
                </>
              ) : (
                <>
                  <Button asChild className="h-11 px-5 text-[15px] font-bold">
                    <Link href={signInHref}>
                      <LogIn aria-hidden className="h-4 w-4" />
                      {t("lockSignIn")}
                    </Link>
                  </Button>
                  <Button asChild className="h-11 px-5 text-[15px] font-bold" variant="secondary">
                    <Link href={`/${locale}/register`}>{t("lockCreateAccount")}</Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {outlineBlock}
      </section>
      )}
    </div>
  );
}
